import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { createKeyboardNav, backspacePrev, arrowNav } from "@/lib/keyboardNav";
import { openTxnPdf } from "@/components/PaymentPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { Search, ArrowLeft, Printer, Layers, Undo2, PackageMinus, Trash2, ChevronDown, Pencil, Minimize2 } from "lucide-react";
import { useMinimized } from "@/context/MinimizedContext";

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };

const FORM_SEL = 'input:not([disabled]), [role="combobox"], [data-line-amt], button[data-testid^="ret-batch-"]:not([disabled]), [data-net-amount]';
const ALLOC_SEL = 'input:not([disabled]), button[role="checkbox"], button[data-testid="save-alloc-btn"]';

// Searchable original-invoice picker (same keyboard contract as ItemCombobox:
// role=combobox + data-auto-open + data-placeholder when empty).
function InvoiceCombobox({ mode, sel, onPick, testId }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const tRef = useRef(null);
  const load = useCallback(async (query) => {
    try {
      const p = new URLSearchParams({ type: mode });
      if (query) p.set("q", query);
      const res = await api.get(`/transactions/returnable?${p}`);
      setList(res.data);
    } catch (e) { toast.error(formatErr(e)); }
  }, [mode]);
  useEffect(() => { if (open) load(""); }, [open, load]);
  useEffect(() => {
    clearTimeout(tRef.current);
    if (!open) return;
    tRef.current = setTimeout(() => load(q.trim()), 250);
    return () => clearTimeout(tRef.current);
  }, [q, open, load]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testId}
          role="combobox" aria-expanded={open} aria-haspopup="listbox" data-auto-open="true" data-placeholder={sel ? undefined : ""}
          className="w-full justify-between rounded-sm bg-secondary border-border h-9 text-xs font-normal">
          <span className="truncate">{sel
            ? <span className="font-mono text-primary">{sel.txn_number}</span>
            : <span className="text-muted-foreground">Search original invoice…</span>}</span>
          <ChevronDown className="w-3 h-3 opacity-50 shrink-0"/>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[420px] bg-card border-border" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Invoice # or party name…" value={q} onValueChange={setQ} />
          <CommandList>
            <CommandEmpty>No invoice found.</CommandEmpty>
            {list.map(t => (
              <CommandItem key={t.id} value={t.id} onSelect={() => { setOpen(false); onPick(t); }}
                className="text-xs" data-testid={`inv-opt-${t.txn_number}`}>
                <span className="font-mono text-primary mr-2">{t.txn_number}</span>
                <span className="text-muted-foreground">{(t.created_at || "").slice(0, 10)} · {t.party_name || "Walk-in"} · {inr(t.total)}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function ReturnForm({ mode, embedded = false, onFullScreenChange }) {
  const isSale = mode === "sale";
  const type = isSale ? "sale_return" : "purchase_return";
  const title = isSale ? "Sale Return" : "Purchase Return";
  const noteLabel = isSale ? "CREDIT NOTE" : "DEBIT NOTE";
  const partyLabel = isSale ? "Customer" : "Supplier";

  // list state
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState(monthAgo());
  const [dateTo, setDateTo] = useState(today());
  // form state
  const [open, setOpen] = useState(false);
  const [orig, setOrig] = useState(null);
  const [form, setForm] = useState(null);
  const [batchIdx, setBatchIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [editId, setEditId] = useState(null);
  const [phone, setPhone] = useState("");
  const [txnDate, setTxnDate] = useState(today());
  const { minimize, consumeRestore, pending } = useMinimized();

  // Restore minimised return form state
  useEffect(() => {
    const p = consumeRestore("txn-return");
    if (!p || p.mode !== mode) return;
    setEditId(p.editId || null);
    setForm(p.form);
    setOrig(p.orig || null);
    setPhone(p.phone || "");
    setTxnDate(p.txnDate || today());
    setOpen(true);
  }, [pending, mode]); // eslint-disable-line

  const minimiseReturn = () => {
    const ref = editId ? (form?.invoice_number || "Edit") : (orig?.txn_number || "New");
    const path = isSale ? "/sale-return" : "/purchase-return";
    const dirty = !!(form && ((form.lines || []).some(l => Number(l.qty) > 0) || form.notes));
    minimize({ kind: "txn-return", label: `${editId ? "Edit" : "New"} ${title}`, ref,
               state: { mode, form, editId, orig, phone, txnDate }, path, dirty });
    setOpen(false); setBatchIdx(null);
    toast.success("Minimised to taskbar");
  };

  const load = useCallback(async () => {
    try {
      const p = new URLSearchParams({ type });
      if (q.trim()) p.set("q", q.trim());
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      const res = await api.get(`/transactions?${p}`);
      setRows(res.data);
    } catch (e) { toast.error(formatErr(e)); }
  }, [type, q, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    document.body.classList.toggle("txn-form-fullscreen", open);
    onFullScreenChange?.(open);
    return () => document.body.classList.remove("txn-form-fullscreen");
  }, [open]); // eslint-disable-line

  const newForm = () => {
    setOrig(null);
    setPhone("");
    setTxnDate(today());
    setForm({ notes: "", lines: [] });
    setEditId(null);
    setBatchIdx(null); setDone(null);
    setOpen(true);
    setTimeout(() => document.querySelector('[data-testid="ret-orig-invoice"]')?.click(), 180);
  };

  // Deep-link edit: ?edit=<id> opens the form pre-filled (used by Transaction Master)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const eid = sp.get("edit");
    if (!eid) return;
    (async () => {
      try { const res = await api.get(`/transactions/${eid}`); openEdit(res.data); }
      catch (e) { toast.error(formatErr(e)); }
      sp.delete("edit");
      window.history.replaceState(null, "", window.location.pathname);
    })();
  }, []); // eslint-disable-line
  const openEdit = async (t) => {
    try {
      const res = await api.get(`/transactions/${t.original_txn_id}/returnable-lines?exclude_txn_id=${t.id}`);
      const d = res.data;
      window.__kbReturnTo = null;
      setOrig(d.txn);
      setPhone(d.txn?.party_phone || t.party_phone || "");
      setTxnDate(t.txn_date || today());
      setEditId(t.id);
      setBatchIdx(null); setDone(null);
      setForm({
        notes: t.notes || "",
        lines: d.lines.filter(l => l.returnable_qty > 0).map(l => {
          const oldLine = (t.lines || []).find(ol => ol.item_id === l.item_id);
          const alloc = l.batches?.length ? l.batches.map(b => {
            const ob = (oldLine?.batches || []).find(x => (x.batch_id && x.batch_id === b.batch_id) || x.batch_number === b.batch_number);
            return { ...b, retQty: ob ? ob.qty : 0, serials: ob?.serial_numbers || [] };
          }) : null;
          return { ...l, return_qty: oldLine ? oldLine.qty : 0, alloc, batches_filled: !l.batches?.length || !!oldLine };
        }),
      });
      setOpen(true);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const pickInvoice = async (t) => {
    try {
      const res = await api.get(`/transactions/${t.id}/returnable-lines`);
      const d = res.data;
      window.__kbReturnTo = null;
      const usable = d.lines.filter(l => l.returnable_qty > 0);
      setOrig(d.txn);
      setPhone(d.txn?.party_phone || "");
      if (!usable.length) toast.warning("This invoice has no returnable items remaining");
      setForm(f => ({
        ...f,
        lines: usable.map(l => ({
          ...l, return_qty: l.returnable_qty, alloc: null, batches_filled: !l.batches?.length,
        })),
      }));
      setTimeout(() => document.querySelector('[data-testid="ret-qty-0"]')?.focus(), 200);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const setQty = (i, v) => {
    setForm(f => ({
      ...f,
      lines: f.lines.map((l, j) => {
        if (j !== i) return l;
        const qty = Math.min(Math.max(Number(v) || 0, 0), Number(l.returnable_qty));
        return { ...l, return_qty: qty, alloc: null, batches_filled: !l.batches?.length };
      }),
    }));
  };
  const delLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));

  // proportional amounts (same pricing as the original invoice; server is authoritative)
  const lineAmt = (l) => (!l.return_qty || !l.billed_qty) ? 0 : (l.total || 0) * (l.return_qty / l.billed_qty);
  const totals = (form?.lines || []).reduce((a, l) => {
    if (!l.return_qty || !l.billed_qty) return a;
    const r = l.return_qty / l.billed_qty;
    return { net: a.net + (l.net || 0) * r, cgst: a.cgst + (l.cgst_amt || 0) * r, sgst: a.sgst + (l.sgst_amt || 0) * r, total: a.total + (l.total || 0) * r };
  }, { net: 0, cgst: 0, sgst: 0, total: 0 });

  // ---- batch/serial allocation overlay ----
  const updAlloc = (li, bi, patch) => {
    setForm(f => ({
      ...f,
      lines: f.lines.map((l, j) => {
        if (j !== li) return l;
        const alloc = l.alloc || l.batches.map(b => ({ ...b, retQty: 0, serials: [] }));
        return { ...l, alloc: alloc.map((a, k) => k === bi ? { ...a, ...patch } : a) };
      }),
    }));
  };
  const allocDone = () => {
    const l = form.lines[batchIdx];
    const alloc = l.alloc || l.batches.map(b => ({ ...b, retQty: 0, serials: [] }));
    const sum = alloc.reduce((a, b) => a + Number(b.retQty || 0), 0);
    if (sum !== Number(l.return_qty)) return toast.error(`Allocated ${sum} of ${l.return_qty} — allocate the full Return Qty`);
    for (const a of alloc) {
      if ((a.serial_numbers || []).length > 0 && Number(a.retQty) > 0 && a.serials.length !== Number(a.retQty))
        return toast.error(`Batch ${a.batch_number}: tick ${a.retQty} serial number(s)`);
    }
    setForm(f => ({ ...f, lines: f.lines.map((x, j) => j === batchIdx ? { ...x, alloc, batches_filled: true } : x) }));
    setBatchIdx(null);
    setTimeout(() => document.querySelector(`[data-testid="ret-batch-${batchIdx}"]`)?.focus(), 120);
  };

  const save = useCallback(async () => {
    if (!orig || !form) return;
    const use = form.lines.filter(l => l.return_qty > 0);
    if (!use.length) return toast.error("Enter Return Qty for at least one item");
    for (const l of use) {
      if (l.batches?.length) {
        const sum = (l.alloc || []).reduce((a, b) => a + Number(b.retQty || 0), 0);
        if (sum !== Number(l.return_qty)) return toast.error(`Allocate batch/serial numbers for ${l.name} — click "Batches"`);
      }
    }
    setSaving(true);
    try {
      const payload = {
        type, party_type: orig.party_type, party_id: orig.party_id,
        party_name: orig.party_name, party_phone: phone || orig.party_phone || null,
        party_address: orig.party_address || null,
        txn_date: txnDate || null,
        store_id: orig.store_id,
        tax_type: orig.tax_type || "GST", bill_calc: orig.bill_calc || "Exclusive",
        original_txn_id: orig.id, original_txn_number: orig.txn_number,
        notes: form.notes || `Return against ${orig.txn_number}`,
        lines: use.map(l => ({
          item_id: l.item_id, qty: Number(l.return_qty), rate: l.rate,
          discount: l.discount || 0, discount_pct: l.discount_pct || 0,
          cgst: l.cgst, sgst: l.sgst,
          ...(l.serial_number && !l.batches?.length ? { serial_number: l.serial_number } : {}),
          ...(l.batches?.length ? {
            batches: l.alloc.filter(a => Number(a.retQty) > 0).map(a => ({
              batch_id: a.batch_id, batch_number: a.batch_number,
              qty: Number(a.retQty), mrp: a.mrp ?? l.rate ?? 0, per_unit_cost: a.per_unit_cost ?? 0,
              cgst: a.cgst ?? l.cgst ?? 0, sgst: a.sgst ?? l.sgst ?? 0, igst: 0, expiry_date: a.expiry_date || null,
              serial_numbers: a.serials || [],
            })),
          } : {}),
        })),
      };
      const res = editId
        ? await api.patch(`/transactions/${editId}`, payload)
        : await api.post("/transactions", payload);
      setDone(res.data);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  }, [orig, form, type, load]);

  // Esc / Ctrl+S like other transaction forms
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (done) return;
        if (batchIdx !== null) { allocDone(); return; }
        save();
        return;
      }
      if (e.key === "Escape") {
        if (done) return;
        if (batchIdx !== null) { setBatchIdx(null); return; }
        if (document.querySelector('[role="listbox"]')) return; // let open dropdowns close natively
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, batchIdx, done, save]); // eslint-disable-line

  const handleEnterNav = createKeyboardNav({
    selector: FORM_SEL,
    endTarget: true,
    skipInside: '[data-testid="batch-entry-page"]',
  });
  const handleAllocNav = (e) => {
    if (e.key === "Backspace") { backspacePrev(e, e.currentTarget, ALLOC_SEL); return; }
    if (e.key.startsWith("Arrow")) { arrowNav(e, e.currentTarget, ALLOC_SEL); return; }
    if (e.key !== "Enter") return;
    const t = e.target;
    if (t.tagName === "TEXTAREA") return;
    const role = t.getAttribute("role");
    if (t.tagName === "BUTTON" && role !== "checkbox") return; // native click (Done)
    e.preventDefault(); e.stopPropagation();
    const fields = [...e.currentTarget.querySelectorAll(ALLOC_SEL)].filter(el => el.offsetParent !== null);
    const idx = fields.indexOf(t);
    if (idx === -1) return;
    if (role === "checkbox") { t.click(); const nx = fields[idx + 1]; if (nx) setTimeout(() => { nx.focus(); if (nx.select) { try { nx.select(); } catch {} } }, 60); return; }
    const next = fields[idx + 1];
    if (next) { next.focus(); if (next.select) { try { next.select(); } catch {} } }
    else document.querySelector('[data-testid="save-alloc-btn"]')?.focus();
  };

  const delReturn = async (t) => {
    if (!window.confirm(`Delete ${t.txn_number}? Stock will be reversed.`)) return;
    try { await api.delete(`/transactions/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const allocLine = open && form && batchIdx !== null ? form.lines[batchIdx] : null;
  const alloc = allocLine ? (allocLine.alloc || allocLine.batches.map(b => ({ ...b, retQty: 0, serials: [] }))) : [];
  const allocSum = alloc.reduce((a, b) => a + Number(b.retQty || 0), 0);
  const anyQty = (form?.lines || []).some(l => l.return_qty > 0);
  const allAlloc = (form?.lines || []).every(l => !(l.batches?.length && l.return_qty > 0) || l.batches_filled && (l.alloc || []).reduce((a, b) => a + Number(b.retQty || 0), 0) === Number(l.return_qty));

  return (
    <div className={open || embedded ? "" : "p-8"} data-testid="return-page">
      {!open && (
        <>
          {!embedded && (
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Transactions · {noteLabel}</p>
              <h1 className="font-display text-2xl font-bold flex items-center gap-2" data-testid="return-title">
                {isSale ? <Undo2 className="w-6 h-6 text-primary"/> : <PackageMinus className="w-6 h-6 text-primary"/>}{title}
              </h1>
            </div>
            <Button data-testid="new-return-btn" onClick={newForm} className="rounded-sm text-xs uppercase tracking-wider">+ New {title}</Button>
          </div>
          )}
          <div className="flex flex-wrap items-end gap-3 mb-4 border border-border bg-card rounded-sm p-3" data-testid="return-filter-bar" onKeyDownCapture={createKeyboardNav({ selector: 'input:not([disabled]):not([type="hidden"])' })}>
            <div className="flex-1 min-w-56">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Search {noteLabel.toLowerCase()} (number / {partyLabel.toLowerCase()})</label>
              <Input data-testid="return-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`${noteLabel} # or party name`} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">From</label>
              <Input type="date" data-testid="return-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">To</label>
              <Input type="date" data-testid="return-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <Button data-testid="return-search-btn" onClick={load} className="rounded-sm"><Search className="w-4 h-4 mr-1"/> Search</Button>
            {embedded && <Button data-testid="new-return-btn" onClick={newForm} className="rounded-sm text-xs uppercase tracking-wider">+ New {title}</Button>}
          </div>
          <div className="border border-border rounded-sm bg-card overflow-hidden">
            <table className="w-full text-sm" data-testid="return-list-table">
              <thead><tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="p-2.5">{noteLabel} #</th><th className="p-2.5">Date</th><th className="p-2.5">Original Invoice</th><th className="p-2.5">{partyLabel}</th><th className="p-2.5 text-right">Total</th><th className="p-2.5 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {rows.map(t => (
                  <tr key={t.id} data-testid={`return-row-${t.id}`} className="border-b border-border/50 hover:bg-secondary/40">
                    <td className="p-2.5 font-mono text-xs text-primary">{t.txn_number}</td>
                    <td className="p-2.5 text-xs">{(t.created_at || "").slice(0, 10)}</td>
                    <td className="p-2.5 font-mono text-xs">{t.original_txn_number || "—"}</td>
                    <td className="p-2.5">{t.party_name || "Walk-in"}</td>
                    <td className="p-2.5 text-right font-mono">{inr(t.total)}</td>
                    <td className="p-2.5 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" data-testid={`edit-return-${t.id}`} onClick={() => openEdit(t)} className="h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5"/></Button>
                      <Button size="sm" variant="ghost" data-testid={`print-return-${t.id}`} onClick={() => openTxnPdf(t.id)} className="h-7 w-7 p-0"><Printer className="w-3.5 h-3.5"/></Button>
                      <Button size="sm" variant="ghost" data-testid={`del-return-${t.id}`} onClick={() => delReturn(t)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3.5 h-3.5"/></Button>
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm" data-testid="return-no-results">No {title.toLowerCase()} notes yet. Click "New {title}".</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {open && form && (
        <div className="min-h-screen bg-background p-6" data-testid="return-form-page" onKeyDownCapture={handleEnterNav}>
          {batchIdx === null ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <div className="flex items-center gap-3">
                  <Button type="button" data-testid="form-back-btn" variant="outline" onClick={() => setOpen(false)} className="rounded-sm text-xs uppercase tracking-wider"><ArrowLeft className="w-3 h-3 mr-1"/>Back to List</Button>
                  <h2 className="font-display uppercase text-lg">{editId ? "Edit" : "New"} {title}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="button" data-testid="minimise-return-btn" variant="outline" onClick={minimiseReturn} className="rounded-sm text-xs uppercase tracking-wider"><Minimize2 className="w-3 h-3 mr-1"/>Minimise</Button>
                  <Button type="button" data-testid="form-cancel-btn" variant="ghost" onClick={() => setOpen(false)} className="rounded-sm text-xs uppercase tracking-wider text-muted-foreground">Cancel</Button>
                </div>
              </div>

              <div className="grid grid-cols-[minmax(0,2.2fr)_repeat(3,minmax(0,1fr))] gap-3">
                {/* Line 1: Original Invoice (wide), Party, Mobile Number, Date · Line 2: Address, Rate, Tax Type, Bill Calculation */}
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Original Invoice Number *</Label>
                  <div className="mt-1">
                    {editId
                      ? <Input data-testid="ret-orig-invoice" value={orig ? `${orig.txn_number} (locked)` : ""} readOnly className="rounded-sm bg-secondary border-border h-9 text-xs font-mono text-primary"/>
                      : <InvoiceCombobox mode={mode} sel={orig} onPick={pickInvoice} testId="ret-orig-invoice"/>}
                  </div>
                  {orig && (
                    <div data-testid="orig-info" className="mt-2 text-[11px] font-mono border border-border bg-secondary/40 rounded-sm p-2 space-y-0.5">
                      <div><span className="text-muted-foreground">Date:</span> {(orig.created_at || "").slice(0, 10)} · <span className="text-muted-foreground">Invoice Total:</span> <span className="text-primary">{inr(orig.total)}</span></div>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{partyLabel}</Label>
                  <Input data-testid="ret-party" value={orig?.party_name || ""} readOnly placeholder="Auto-filled from invoice" className="mt-1 rounded-sm bg-secondary border-border text-muted-foreground"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mobile Number</Label>
                  <Input data-testid="ret-mobile" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ""))} placeholder="Auto-filled from invoice" className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date</Label>
                  <Input type="date" data-testid="ret-date" value={txnDate} onChange={(e) => setTxnDate(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border"/>
                </div>
              </div>
              <div className="grid grid-cols-[minmax(0,2.2fr)_repeat(3,minmax(0,1fr))] gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label>
                  <Input data-testid="ret-address" value={orig?.party_address || ""} readOnly placeholder="Auto-filled from invoice" className="mt-1 rounded-sm bg-secondary border-border text-muted-foreground"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Rate (Rate Master)</Label>
                  <Input data-testid="ret-rate-type" value="Original invoice rates" readOnly disabled title="Return lines are priced from the original invoice" className="mt-1 rounded-sm bg-secondary border-border text-muted-foreground"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Tax Type</Label>
                  <Input data-testid="ret-tax-type" value={orig?.tax_type || "GST"} readOnly className="mt-1 rounded-sm bg-secondary border-border text-muted-foreground"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Bill Calculation</Label>
                  <Input data-testid="ret-bill-calc" value={orig?.bill_calc || "Exclusive"} readOnly className="mt-1 rounded-sm bg-secondary border-border text-muted-foreground"/>
                </div>
              </div>

              {!orig && (
                <div className="border border-dashed border-border rounded-sm p-10 text-center text-muted-foreground text-sm" data-testid="return-empty-hint">
                  Search and select the original {isSale ? "sale" : "purchase"} invoice above to load its item lines.
                </div>
              )}

              {orig && (
                <div className="space-y-2">
                  <div className="grid grid-cols-[minmax(160px,1.7fr)_90px_100px_88px_84px_64px_64px_64px_96px_118px_36px] gap-2 items-end px-[9px] text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                    <div className="text-center">Item</div>
                    <div className="text-center">Return Qty *</div>
                    <div className="text-center">Part No</div>
                    <div className="text-center">HSN</div>
                    <div className="text-center">Rate</div>
                    <div className="text-center">Disc %</div>
                    <div className="text-center">CGST %</div>
                    <div className="text-center">SGST %</div>
                    <div className="text-center">Billed Qty</div>
                    <div className="text-center">Amount</div>
                    <div></div>
                  </div>
                  {form.lines.map((l, i) => (
                    <div key={i} data-testid={`ret-line-${i}`} data-kb-row={i} className={`border rounded-sm p-2 ${l.batches_filled && l.return_qty > 0 ? "border-green-500/40 bg-green-500/5" : "border-border bg-secondary/20"}`}>
                      <div className="grid grid-cols-[minmax(160px,1.7fr)_90px_100px_88px_84px_64px_64px_64px_96px_118px_36px] gap-2 items-center">
                        <div className="text-xs px-1 truncate" data-testid={`ret-item-${i}`} title={l.name}>{l.name}{l.batches?.length ? <span className="text-muted-foreground"> · batch</span> : ""}</div>
                        <Input data-testid={`ret-qty-${i}`} type="number" min="0" max={l.returnable_qty} value={l.return_qty} onChange={(e) => setQty(i, e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right"/>
                        <Input data-testid={`ret-partno-${i}`} value={l.part_no || ""} readOnly className="rounded-sm bg-secondary border-border h-9 text-xs font-mono text-muted-foreground"/>
                        <Input data-testid={`ret-hsn-${i}`} value={l.hsn_code || ""} readOnly className="rounded-sm bg-secondary border-border h-9 text-xs font-mono text-muted-foreground"/>
                        <Input data-testid={`ret-mrp-${i}`} value={l.rate} readOnly className="rounded-sm bg-secondary border-border h-9 text-xs text-right text-muted-foreground"/>
                        <Input data-testid={`ret-disc-${i}`} value={l.discount_pct || 0} readOnly className="rounded-sm bg-secondary border-border h-9 text-xs text-right text-muted-foreground"/>
                        <Input data-testid={`ret-cgst-${i}`} value={l.cgst || 0} readOnly className="rounded-sm bg-secondary border-border h-9 text-xs text-right text-muted-foreground"/>
                        <Input data-testid={`ret-sgst-${i}`} value={l.sgst || 0} readOnly className="rounded-sm bg-secondary border-border h-9 text-xs text-right text-muted-foreground"/>
                        <Input data-testid={`ret-billed-${i}`} value={`${l.returnable_qty} / ${l.billed_qty}`} readOnly title="Returnable / Billed" className="rounded-sm bg-secondary border-border h-9 text-xs text-right text-muted-foreground"/>
                        <div data-testid={`ret-amount-${i}`} data-line-amt tabIndex={-1} className="text-right font-mono text-xs text-primary rounded-sm px-1 focus:outline-none focus:ring-1 focus:ring-primary">{inr(lineAmt(l))}</div>
                        <div className="flex items-center gap-1">
                          {l.batches?.length > 0 && (
                            <Button type="button" data-testid={`ret-batch-${i}`} onClick={() => setBatchIdx(i)}
                              disabled={!(Number(l.return_qty) > 0)}
                              className={`rounded-sm text-[10px] uppercase tracking-wider h-9 whitespace-nowrap ${l.batches_filled && l.return_qty > 0 ? "bg-green-600 hover:bg-green-600 text-white" : "bg-primary text-primary-foreground"}`}>
                              <Layers className="w-3 h-3 mr-1"/>{l.batches_filled && l.return_qty > 0 ? "Allocated" : "Batches"}
                            </Button>
                          )}
                          <Button type="button" size="sm" variant="ghost" data-testid={`ret-del-${i}`} onClick={() => delLine(i)} className="h-9 w-9 p-0 text-destructive"><Trash2 className="w-4 h-4"/></Button>
                        </div>
                      </div>
                      {l.batches_filled && l.alloc && l.return_qty > 0 && (
                        <div data-testid={`ret-line-summary-${i}`} className="mt-2 text-xs font-mono text-green-500 flex items-center gap-3 flex-wrap">
                          <span>{l.alloc.filter(a => Number(a.retQty) > 0).length} batch(es) · {l.alloc.reduce((a, b) => a + Number(b.retQty || 0), 0)} unit(s)</span>
                          <span className="text-muted-foreground">{l.alloc.filter(a => Number(a.retQty) > 0).map(b => b.batch_number).join(", ")}</span>
                          {l.alloc.some(a => a.serials?.length) && <span className="text-primary">{l.alloc.reduce((a, b) => a + (b.serials?.length || 0), 0)} serial(s) returned</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {orig && (
                <div className="grid grid-cols-2 gap-4 border-t border-border pt-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Notes</Label>
                    <Textarea data-testid="ret-notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Net Amount</span><span className="font-mono" data-testid="totals-net">{inr(totals.net)}</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>CGST</span><span className="font-mono" data-testid="totals-cgst">{inr(totals.cgst)}</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>SGST</span><span className="font-mono" data-testid="totals-sgst">{inr(totals.sgst)}</span></div>
                    <div className="flex justify-between border-t border-border pt-2 items-center">
                      <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Grand Total</span>
                      <span data-testid="totals-grand" data-end-target data-net-amount tabIndex={-1} className="font-display font-black text-2xl text-primary rounded-sm px-1 focus:outline-none focus:ring-2 focus:ring-primary">{inr(totals.total)}</span>
                    </div>
                  </div>
                </div>
              )}

              {orig && anyQty && !allAlloc && (
                <div data-testid="save-blocked-hint" className="text-[11px] uppercase tracking-wider text-yellow-500 border border-yellow-500/30 bg-yellow-500/5 rounded-sm p-2">
                  Allocate batches / serial numbers for all returned batch items before saving
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" data-testid="return-form-cancel2" onClick={() => setOpen(false)} className="rounded-sm text-xs uppercase tracking-wider">Cancel</Button>
                <Button type="button" data-testid="save-return-btn" onClick={save} disabled={saving || !orig || !anyQty} className="rounded-sm text-xs uppercase tracking-wider">
                  {saving ? "Saving..." : `Save ${title} (Ctrl+S)`}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="batch-entry-page" onKeyDownCapture={handleAllocNav}>
              <div className="flex items-center justify-between border-b border-border pb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{title} · Return Batch Entry</p>
                  <h2 className="font-display uppercase text-lg" data-testid="alloc-item-name">{allocLine?.name}</h2>
                </div>
                <div className="text-xs text-muted-foreground">Return Qty to allocate: <span className="font-mono text-primary" data-testid="alloc-target">{allocLine?.return_qty}</span></div>
              </div>
              <div className="space-y-2 max-w-3xl">
                {alloc.map((a, bi) => (
                  <div key={bi} className="border border-border rounded-sm p-3 bg-secondary/20" data-testid={`alloc-row-${bi}`} data-kb-row={bi}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs">
                        <span className="font-mono text-primary">{a.batch_number || "—"}</span>
                        <span className="text-muted-foreground"> · billed {a.qty}{a.mrp ? ` · MRP ${inr(a.mrp)}` : ""}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Return Qty</span>
                        <Input type="number" min="0" max={Math.min(Number(a.qty), Number(allocLine.return_qty))} data-testid={`alloc-qty-${bi}`} value={a.retQty}
                          onChange={(e) => updAlloc(batchIdx, bi, { retQty: Math.min(Math.max(Number(e.target.value) || 0, 0), Math.min(Number(a.qty), Number(allocLine.return_qty))) })}
                          className="h-8 w-24 text-right rounded-sm bg-secondary border-border"/>
                      </div>
                    </div>
                    {(a.serial_numbers || []).length > 0 && Number(a.retQty) > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {a.serial_numbers.map(sn => (
                          <label key={sn} className="flex items-center gap-1.5 text-xs border border-border rounded-sm px-2 py-1">
                            <Checkbox data-testid={`alloc-serial-${bi}-${sn}`} checked={a.serials.includes(sn)}
                              onCheckedChange={(c) => updAlloc(batchIdx, bi, { serials: c ? [...a.serials, sn] : a.serials.filter(x => x !== sn) })}/>
                            <span className="font-mono">{sn}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between max-w-3xl">
                <span className={`text-xs ${allocSum === Number(allocLine?.return_qty) ? "text-green-400" : "text-yellow-400"}`} data-testid="alloc-status">Allocated {allocSum} / {allocLine?.return_qty}</span>
                <Button data-testid="save-alloc-btn" onClick={allocDone} className="rounded-sm text-xs uppercase tracking-wider">Done</Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!done} onOpenChange={() => {}}>
        <DialogContent className="rounded-sm bg-card border-border max-w-sm text-center" onKeyDownCapture={createKeyboardNav({ selector: 'input:not([disabled])' })}>
          <DialogHeader><DialogTitle className="font-display uppercase text-center">{noteLabel} — Saved</DialogTitle></DialogHeader>
          {done && (
            <div data-testid="return-done-summary">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{title} Reference</p>
              <p className="font-mono text-2xl text-primary font-bold" data-testid="return-note-number">{done.txn_number}</p>
              <p className="text-xs text-muted-foreground mt-1">against invoice <span className="font-mono">{done.original_txn_number}</span></p>
              <div className="border border-border rounded-sm mt-4 divide-y divide-border text-sm">
                <div className="flex justify-between p-2"><span className="text-muted-foreground">Net Amount</span><span className="font-mono">{inr(done.subtotal)}</span></div>
                <div className="flex justify-between p-2"><span className="text-muted-foreground">CGST</span><span className="font-mono">{inr(done.cgst_total)}</span></div>
                <div className="flex justify-between p-2"><span className="text-muted-foreground">SGST</span><span className="font-mono">{inr(done.sgst_total)}</span></div>
                <div className="flex justify-between p-2 font-bold"><span>Grand Total</span><span className="font-mono text-primary" data-testid="return-note-total">{inr(done.total)}</span></div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">
                {isSale ? "Credit can be adjusted against future invoices or refunded to the customer." : "Debit note issued to the supplier for the returned goods."}
              </p>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" className="flex-1 rounded-sm" data-testid="return-print-btn" onClick={() => openTxnPdf(done.id)}><Printer className="w-4 h-4 mr-1"/> Print</Button>
                <Button className="flex-1 rounded-sm" data-testid="return-new-btn" onClick={() => { setDone(null); newForm(); }}>New {title}</Button>
                <Button variant="ghost" className="rounded-sm" data-testid="return-close-btn" onClick={() => { setDone(null); setOpen(false); }}>List</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
